import { computed, type Ref } from 'vue';

export interface SelectableItem<Key extends string = string> {
  key: Key;
  disabled?: boolean;
}

export interface UseSelectableListOptions<Key extends string = string> {
  modelValue: Ref<Key>;
  items: Ref<SelectableItem<Key>[]>;
  onChange?: (value: Key) => void;
}

export function useSelectableList<Key extends string = string>(
  options: UseSelectableListOptions<Key>,
) {
  const { modelValue, items, onChange } = options;

  const activeKey = computed<Key>({
    get: () => modelValue.value,
    set: (value: Key) => {
      if (value === modelValue.value) return;
      modelValue.value = value;
      if (onChange) onChange(value);
    },
  });

  function isDisabled(key: Key): boolean {
    const item = items.value.find(i => i.key === key);
    return Boolean(item?.disabled);
  }

  function select(key: Key): void {
    if (isDisabled(key)) return;
    activeKey.value = key;
  }

  return {
    activeKey,
    isDisabled,
    select,
  } as const;
}

export type UseSelectableListReturn<Key extends string = string> = ReturnType<
  typeof useSelectableList<Key>
>;
